import { useId } from "react";

/**
 * Two handles on a clock that counts down, left to right: from `max` to 0:00.
 * `from` is the left handle (more time left), `to` the right one.
 */
export function ClockRange({
  max,
  from,
  to,
  onChange,
  format,
  labelFrom,
  labelTo,
}: {
  max: number;
  from: number;
  to: number;
  onChange: (from: number, to: number) => void;
  format: (seconds: number) => string;
  labelFrom: string;
  labelTo: string;
}) {
  const id = useId();
  const a = max - from;
  const b = max - to;
  // A round mark near the middle: 1:00 on the round clock, 0:20 on the bomb timer.
  const mid = Math.round(max / 10) * 5;
  const fill = { left: `calc(9px + (100% - 18px) * ${a / max})`, width: `calc((100% - 18px) * ${(b - a) / max})` };
  return (
    <div className="clock-range-wrap">
      <output className="clock-range-value mono" htmlFor={`${id}-a ${id}-b`}>
        {format(from)} → {format(to)}
      </output>
      <div className="clock-range">
        <div className="clock-range-track" aria-hidden="true">
          <span className="clock-range-fill" style={fill} />
        </div>
        <input
          id={`${id}-a`}
          type="range"
          min={0}
          max={max}
          step={1}
          value={a}
          aria-label={labelFrom}
          aria-valuetext={format(from)}
          onChange={(e) => onChange(max - Math.min(Number(e.target.value), b), to)}
        />
        <input
          id={`${id}-b`}
          type="range"
          min={0}
          max={max}
          step={1}
          value={b}
          aria-label={labelTo}
          aria-valuetext={format(to)}
          onChange={(e) => onChange(from, max - Math.max(Number(e.target.value), a))}
        />
      </div>
      <div className="clock-range-scale muted small" aria-hidden="true">
        <span style={{ left: 0 }}>{format(max)}</span>
        <span style={{ left: `calc(9px + (100% - 18px) * ${(max - mid) / max})`, transform: "translateX(-50%)" }}>{format(mid)}</span>
        <span style={{ right: 0 }}>{format(0)}</span>
      </div>
    </div>
  );
}
